import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthCallback = ({ onAuthSuccess, onAuthError }) => {
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing...');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        console.log('Processing Google Auth callback...');
        setStatus('Extracting session...');
        
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        console.log('URL hash:', hash);
        
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          throw new Error('No session ID found in URL. Please try signing in again.');
        }

        const sessionId = sessionIdMatch[1];
        console.log('Session ID found:', sessionId.substring(0, 10) + '...');

        // Use our backend proxy to exchange session_id (avoids CORS)
        setStatus('Verifying with Google...');
        console.log('Using backend proxy for Google auth...');
        
        const response = await axios.post(
          `${API_URL}/api/auth/google-callback`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        console.log('Auth response:', response.data);
        
        if (!response.data.success) {
          throw new Error(response.data.detail || 'Authentication failed');
        }

        const userData = response.data.user;
        console.log('User authenticated:', { email: userData.email, name: userData.name });

        // Prepare user data for frontend
        const userForState = {
          user_id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture,
          auth_type: 'google'
        };

        // Save to localStorage for persistence
        localStorage.setItem('flight380_user', JSON.stringify(userForState));
        console.log('User saved to localStorage');

        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);

        // Notify parent of success
        setStatus('Welcome!');
        console.log('Calling onAuthSuccess...');
        onAuthSuccess(userForState);

      } catch (error) {
        console.error('Auth callback error:', error);
        console.error('Error response:', error.response?.data);
        
        // Determine error message
        let errorMsg = 'Authentication failed. Please try again.';
        
        if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.message.includes('Network')) {
          errorMsg = 'Unable to connect to server. Please check your internet connection.';
        } else if (error.message.includes('session ID')) {
          errorMsg = error.message;
        } else if (error.message) {
          errorMsg = error.message;
        }
        
        setError(errorMsg);
        
        // Clear hash and notify parent
        window.history.replaceState(null, '', window.location.pathname);
        localStorage.removeItem('flight380_user');
        
        // Delay error callback to show error message
        setTimeout(() => {
          onAuthError(errorMsg);
        }, 4000);
      }
    };

    processAuth();
  }, [onAuthSuccess, onAuthError]);

  if (error) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <p className="text-xl font-semibold text-slate-800 mb-2">Sign In Failed</p>
          <p className="text-sm text-slate-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="bg-brand-600 text-white px-6 py-2 rounded-lg hover:bg-brand-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-brand-600 mx-auto mb-4" />
        <p className="text-lg text-slate-600">Completing sign in...</p>
        <p className="text-sm text-slate-400 mt-2">{status}</p>
      </div>
    </div>
  );
};
